/**
 * GPU 后端选择（DESIGN.md §2.8）。
 *
 * 策略：WebGPU 优先、WebGL2 回退、两者皆无则返回 `null`（调用方走 CPU 路径）。
 *
 * 本文件**不引用任何后端特有类型**——它只负责按顺序尝试工厂函数。
 * 这样"选择逻辑"本身可以在没有任何 GPU 的环境里被完整测试。
 */
import type { GpuBackend } from './backend';
import { createWebGPUBackend } from './webgpu/backend';
import { createWebGL2Backend } from './webgl2/backend';

/** 强制指定后端的环境变量名（Vite 在构建期内联 `import.meta.env`）。 */
export const FORCE_BACKEND_ENV = 'VITE_GPU_BACKEND';

export type BackendPreference = 'auto' | 'webgpu' | 'webgl2';

/** 后端工厂，便于测试注入。两者都约定"不可用返回 null，不抛异常"。 */
export interface BackendFactories {
  webgpu(): Promise<GpuBackend | null>;
  webgl2(): GpuBackend | null;
}

const DEFAULT_FACTORIES: BackendFactories = {
  webgpu: createWebGPUBackend,
  webgl2: () => createWebGL2Backend(),
};

/**
 * 读取强制后端偏好。
 *
 * 存在的意义是**让 WebGL2 路径能被真实测试**：只在 WebGPU 不可用的机器上才跑
 * 回退分支，等于这条路径在开发机上永远得不到验证，等用户报障时才发现坏了。
 */
export function readPreference(env?: Record<string, string | undefined>): BackendPreference {
  const source = env ?? (import.meta.env as unknown as Record<string, string | undefined>);
  const raw = source[FORCE_BACKEND_ENV]?.toLowerCase();
  if (raw === 'webgpu' || raw === 'webgl2') return raw;
  return 'auto';
}

export interface SelectBackendOptions {
  readonly preference?: BackendPreference;
  readonly factories?: BackendFactories;
}

/**
 * 选择并创建 GPU 后端。
 *
 * @returns 可用后端；两条路径都不可用时返回 `null`。**任何情况下不抛异常**——
 *   "这台设备没有 GPU"是产品必须支持的正常状态，不是错误。
 */
export async function selectBackend(options: SelectBackendOptions = {}): Promise<GpuBackend | null> {
  const factories = options.factories ?? DEFAULT_FACTORIES;
  const preference = options.preference ?? readPreference();

  if (preference === 'webgl2') return factories.webgl2();

  if (preference === 'webgpu') {
    // 显式指定 webgpu 时不静默回退：测试要的是"确实跑在 WebGPU 上"。
    return await factories.webgpu();
  }

  const webgpu = await factories.webgpu();
  if (webgpu !== null) return webgpu;
  return factories.webgl2();
}
